import { DeleteButton, EditButton, Show } from "@refinedev/antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import { Image, notification, Switch, Table, Tag, Typography } from "antd";
import { CommunityDTO, UserDTO } from "interfaces";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  deleteUserExclusionFromCommunityRankingsUseCase,
} from "usecases/user/deleteUserExclusionFromCommunityRankings";
import { excludeUserFromCommunityRankingsUseCase } from "usecases/user/excludeUserFromCommunityRankings";
import { getCommunityExcludedUsersUseCase } from "usecases/user/getCommunityExcludedUsersUseCase";
import { getUserFollowingCommunitiesUseCase } from "usecases/user/getUserFollowingCommunitiesUseCase";
import { Gender } from "../../utils/constants";

const { Title, Text } = Typography;

interface FollowingCommunity {
  id: string;
  name: string;
  isExcludedFromRanking: boolean;
}

function formatDate(date?: string) {
  if (!date) {
    return "-";
  }

  const userLocale = navigator.language || "es-ES";

  const options: any = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  };

  return new Intl.DateTimeFormat(userLocale, options).format(new Date(date));
}

export const UserShow: React.FC<IResourceComponentsProps> = () => {
  const { queryResult } = useShow<UserDTO>({
    queryOptions: {
      retry: 0,
    },
  });
  const { data, isLoading, error } = queryResult;

  const navigate = useNavigate();

  if (error) {
    navigate("/users/find");
  }

  const record = data?.data;

  const [isDeleted, setIsDeleted] = useState(Boolean(record?.deletedAt));
  const [followingCommunities, setFollowingCommunities] = useState<
    FollowingCommunity[]
  >([]);

  useEffect(() => {
    const fetchFollowingCommunitiesData = async () => {
      if (record?.id && !followingCommunities.length) {
        try {
          await fetchUserFollowingCommunities(record.id);
        } catch (error: any) {
          notification.error({
            message: "Unable to get user's following communities",
          });
        }
      }
    };

    fetchFollowingCommunitiesData();
  }, [record?.id]);

  const fetchUserFollowingCommunities = async (userId: string) => {
    const followingCommunitiesRequest =
      await getUserFollowingCommunitiesUseCase(userId);
    const followingCommunities = followingCommunitiesRequest.data;

    const communityIds = followingCommunities.map(
      (community: CommunityDTO) => community.id
    );

    let activeRankingCommunities: {
      id: string;
      isExcludedFromRanking: boolean;
    }[] = [];
    if (communityIds.length > 0) {
      const activeRankingCommunitiesRequests = await Promise.all(
        communityIds.map(async (communityId: string) => {
          const response = await getCommunityExcludedUsersUseCase(communityId, [
            userId,
          ]);
          return {
            id: communityId,
            isExcludedFromRanking: !!response.data.userIds?.length,
          };
        })
      );
      activeRankingCommunities = activeRankingCommunitiesRequests;
    }

    followingCommunities.forEach((community: any) => {
      community.isExcludedFromRanking = activeRankingCommunities.find(
        (communityExcluded) => communityExcluded.id === community.id
      )?.isExcludedFromRanking;
    });

    setFollowingCommunities([...followingCommunities]);
  };

  const onFreezeChange = async (checked: boolean, communityId: string) => {
    try {
      const useCase = checked
        ? excludeUserFromCommunityRankingsUseCase
        : deleteUserExclusionFromCommunityRankingsUseCase;

      await useCase(communityId, record?.id as string);

      const rankingStatusIndexToBeUpdated = followingCommunities.findIndex(
        (obj) => obj.id == communityId
      );
      const updatedFollowingCommunities = [...followingCommunities];

      updatedFollowingCommunities[
        rankingStatusIndexToBeUpdated
      ].isExcludedFromRanking = checked;

      setFollowingCommunities(updatedFollowingCommunities);
    } catch {
      notification.error({
        message: "Unable to update user ranking freeze status",
      });
    }
  };

  return (
    <Show
      isLoading={isLoading}
      recordItemId={record?.id}
      headerButtons={
        record?.deletedAt ? (
          <></>
        ) : (
          <>
            <EditButton />
            <DeleteButton
              recordItemId={record?.id}
              onSuccess={() => {
                setIsDeleted(true);
              }}
              confirmTitle="User will not be able to login again and their phone will be deleted. Are you sure?"
            />
          </>
        )
      }
    >
      {isDeleted && <Tag color="red">User Deleted</Tag>}
      <Title level={1}>{record?.username || "-"}</Title>

      <Title level={5}>Id</Title>
      <Text>{record?.id}</Text>

      <Title level={ 5 }>Name</Title>
      <Text>{ record?.name }</Text>

      <Title level={ 5 }>Last name</Title>
      <Text>{ record?.lastname }</Text>

      <Title level={5}>Phone</Title>
      <Text>{record?.phone || "-"}</Text>

      <Title level={ 5 }>Email</Title>
      <Text>{ record?.email || "-" }</Text>

      <Title level={5}>Country Code</Title>
      <Text>{record?.countryCode}</Text>

      <Title level={5}>Verified</Title>
      <Text>{record?.verified.toString() || "false"}</Text>

      <Title level={ 5 }>Country</Title>
      <Text>{ record?.country }</Text>

      <Title level={ 5 }>Region(State)</Title>
      <Text>{ record?.state }</Text>

      <Title level={ 5 }>City</Title>
      <Text>{ record?.city }</Text>

      <Title level={ 5 }>Gender</Title>
      <Text>{ Gender[record?.gender as keyof typeof Gender] }</Text>

      <Title level={ 5 }>Birthdate</Title>
      <Text>{ record?.birthday }</Text>

      <Title level={ 5 }>Age</Title>
      <Text>{ record?.age }</Text>

      <Title level={ 5 }>Sesh Role</Title>
      <Text>{record?.role || "-"}</Text>

      <Title level={5}>Onboarding Completed</Title>
      <Text>{record?.onboardingCompleted?.toString()}</Text>

      <Title level={5}>Date of deletion</Title>
      <Text>{formatDate(record?.deletedAt)}</Text>

      <Title level={5}>Image</Title>
      {record?.image?.webp?.thumbnail ? (
        <Image src={record?.image?.webp?.thumbnail} />
      ) : (
        <Text>{record?.image || "No image"}</Text>
      )}

      {followingCommunities.length > 0 && (
        <>
          <Title level={5} style={{ marginTop: "3rem" }}>
            Following Communities
          </Title>
          <Table dataSource={followingCommunities} rowKey="id">
            <Table.Column
              dataIndex="name"
              title="Name"
              filters={followingCommunities.map((community) => ({
                text: community.name,
                value: community.name,
              }))}
              filterSearch={true}
              onFilter={(value, record: any) => record.name.startsWith(value)}
            />
            <Table.Column
              title="Freeze points"
              width={"5%"}
              render={(community) => {
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Switch
                      checked={community.isExcludedFromRanking}
                      size="small"
                      onChange={(value) => onFreezeChange(value, community.id)}
                    />
                  </div>
                );
              }}
            />
          </Table>
        </>
      )}
    </Show>
  );
};
